import React, { useState, useCallback, useRef } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';
import { useApi, API_BASE_URL } from '../../../api/useApi';

interface SystemTabProps {
  brand: BrandConfig;
  Icons: IconsType;
}

interface CacheRefreshResponse {
  success: boolean;
  stats: {
    name: string;
    loaded: boolean;
    ageMs: number;
  };
}

const SystemTab: React.FC<SystemTabProps> = ({ brand, Icons }) => {
  const { post } = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastAgeMs, setLastAgeMs] = useState<number | null>(null);
  const [cooldown, setCooldown] = useState(false);
  const cooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleRefresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    setLastAgeMs(null);
    try {
      const result = await post<CacheRefreshResponse>(
        `${API_BASE_URL}/api/admin/cache/auth0-users/refresh`
      );
      setLastAgeMs(result.stats.ageMs);
      setCooldown(true);
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
      cooldownTimerRef.current = setTimeout(() => setCooldown(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh cache');
    } finally {
      setLoading(false);
    }
  }, [post]);

  const formatAge = (ms: number): string => {
    const seconds = Math.round(ms / 1000);
    return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  };

  return (
    <div
      className="bg-white rounded-lg border p-6"
      style={{ borderColor: brand.colors.border }}
    >
      <div className="max-w-2xl">
        <div className="flex items-start gap-4 mb-6">
          <div
            className="w-12 h-12 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
          >
            <Icons.Settings className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-lg font-medium" style={{ color: brand.colors.secondary }}>
              Auth0 User Cache
            </h3>
            <p className="text-sm mt-1" style={{ color: brand.colors.textLight }}>
              The system caches Auth0 user data locally for faster lookups. Use the button below to force a refresh of the cached user list.
            </p>
          </div>
        </div>

        <div
          className="p-4 rounded-lg mb-6"
          style={{ backgroundColor: brand.colors.bgLight }}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium" style={{ color: brand.colors.text }}>
                Cache Status
              </div>
              {lastAgeMs !== null && (
                <p
                  className="text-sm mt-1"
                  style={{ color: brand.colors.textLight }}
                  data-testid="cache-status-message"
                >
                  Cache refreshed &mdash; last loaded {formatAge(lastAgeMs)} ago
                </p>
              )}
              {error && (
                <p
                  className="text-sm mt-1 text-red-600"
                  data-testid="cache-status-message"
                >
                  {error}
                </p>
              )}
            </div>
            <button
              onClick={handleRefresh}
              disabled={loading || cooldown}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
              data-testid="refresh-auth0-cache-button"
            >
              {loading ? 'Refreshing...' : 'Refresh Cache'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemTab;
